import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Stack — вертикальная колонка с шагом из РАЗРЕШЁННОЙ шкалы отступов
 * (Earth Tech DS v1, layout-слой: базовый модуль 4px, ритм 8px).
 *
 * Зачем примитив, а не `space-y-*` руками: шкала зашита в варианты, поэтому
 * подобрать «на глаз» ступень вне шкалы или произвольные пиксели нельзя —
 * компилятор не даст.
 * Расстояние между секциями задаёт КОНТЕЙНЕР, а не сами секции: детям Stack
 * не нужны собственные `mt-*` / `mb-*` (см. гард `layout-spacing.test.ts`).
 *
 * ```tsx
 * <Stack gap={6}>
 *   <Card>…</Card>
 *   <Card>…</Card>
 * </Stack>
 * ```
 */
const stackVariants = cva('flex flex-col', {
  variants: {
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
      8: 'gap-8',
      12: 'gap-12',
      16: 'gap-16',
    },
  },
  defaultVariants: { gap: 4 },
});

export type StackGap = NonNullable<VariantProps<typeof stackVariants>['gap']>;

export interface StackProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stackVariants> {}

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ className, gap, ...props }, ref) => (
    <div ref={ref} className={cn(stackVariants({ gap }), className)} {...props} />
  ),
);
Stack.displayName = 'Stack';

export { stackVariants };
